// ============================================================================
// /broiler/feed autosave — the owner-scoped pieces of BroilerFeedView's four
// confirmed autosaves (three poultry order fields + one physical count form)
// ============================================================================
// Owner direction 2026-10-24 (site-wide autosave): the month's poultry orders
// and the physical count were Save Order / Save Count buttons that published
// optimistically and voided the write. They are confirmed autosaves now, each
// the shared confirmedDraftAutosave controller, unchanged. What this module
// adds is what that controller cannot know:
//
//   • WHO a draft belongs to. A draft carries the subject, effective role,
//     preview role and session epoch it was typed under (BroilerFeedOwner). A
//     new owner gets a NEW controller, so a queued draft of account A can
//     never be persisted under B, and A's late ack or failure lands on A's
//     suspended controller, which reports nothing.
//   • what an UNKNOWN outcome obliges. A request whose reply was lost may
//     still have landed, or may still land. It is held with its exact request
//     id and payload, and replayed — same id, so the server's receipt answers
//     it — before any newer intent goes out (createHeldReplaySender). A retry
//     never mints a new id.
//   • what the header may say. The keyed saveStatus store follows the
//     controller's states, never a persist call, so "Saved" cannot cover a
//     pending or invalid newer revision, a mount reports nothing, and a stale
//     owner cannot clear a newer save on the same key.
//
// Both writes go through the migration-249 audited operation's TYPED outcome —
// never a boolean collapse: an unconfirmed write is held and replayed, a
// definite refusal settles with its reason, and the two must not be
// indistinguishable to the page. The poultry patch names only the three maps
// this surface owns (never pig), and the count entry keeps the operator's
// typed date and delivery flag rather than re-dating an older count.
// ============================================================================
import {useLayoutEffect, useMemo, useRef, useState} from 'react';
import {useAppDraftGuard} from '../app/navigation/index.js';
import {scopeOrNull} from '../app/data/routeDataCatalog.js';
import type {AuthStateInput} from '../app/data/routeDataCatalog.js';
import {createConfirmedDraftAutosave} from '../shared/confirmedDraftAutosave.js';
import type {DraftSaveState} from '../shared/confirmedDraftAutosave.js';
import {forgetSave, isLatestSave, reportSaveFailed, reportSaved, reportSaving} from '../lib/saveStatus.js';
import type {SaveStatusSink} from '../lib/appStoreBlobApi.js';
import {
  appStoreConfigRejectionMessage,
  newAppStoreConfigRequestId,
  POULTRY_FEED_INVENTORY_APP_STORE_KEY,
  patchFeedOrdersOnServer,
  runAppStoreConfigOperationWithStatus,
} from '../lib/appStoreConfigOperationApi.js';
import type {AppStoreConfigOpOutcome} from '../lib/appStoreConfigOperationApi.js';
import {planFeedOrdersSave, readFreshFeedOrders} from '../lib/feedOrdersApi.js';

// The roles app_store_config_operation admits for feed_orders.patch and
// poultry_feed_inventory.save (migration 249, kept by 253-255). /broiler/feed
// is reachable only by these roles today; the client check is what keeps a
// role PREVIEW honest and stops a draft being scheduled for a write the
// server will refuse.
export const BROILER_FEED_WRITE_ROLES: readonly string[] = Object.freeze(['admin', 'management', 'farm_team']);

export type BroilerFeedOrderKey = 'starter' | 'grower' | 'layerfeed';

export type BroilerFeedOwner = Readonly<{
  key: string;
  subjectId: string;
  effectiveRole: string;
  previewRole: string | null;
  sessionEpoch: number;
}>;

/** The owner a draft typed now belongs to, or null while auth is not a
 * resolved, authenticated epoch (nothing may be scheduled then).
 * `profileResolved` is `profileStatus === PROFILE_STATUS.RESOLVED`, decided by
 * the caller so this module stays free of the auth runtime. */
export function broilerFeedOwner(
  authState: unknown,
  rolePreview: unknown,
  profileResolved: boolean,
  sessionEpoch: unknown,
): BroilerFeedOwner | null {
  if (!profileResolved) return null;
  if (typeof sessionEpoch !== 'number' || !Number.isSafeInteger(sessionEpoch) || sessionEpoch <= 0) return null;
  const scope = scopeOrNull(authState as AuthStateInput, rolePreview);
  if (!scope || scope.kind !== 'authenticated') return null;
  return Object.freeze({
    key: [scope.subjectId, scope.effectiveRole, scope.previewRole ?? '', sessionEpoch].join('|'),
    subjectId: scope.subjectId,
    effectiveRole: scope.effectiveRole,
    previewRole: scope.previewRole,
    sessionEpoch,
  });
}

export function canWriteBroilerFeed(owner: BroilerFeedOwner | null | undefined): owner is BroilerFeedOwner {
  return !!owner && BROILER_FEED_WRITE_ROLES.includes(owner.effectiveRole);
}

/** True while `draftOwner` is still the current, write-capable owner. */
export function isCurrentBroilerFeedWriter(
  draftOwner: BroilerFeedOwner | null | undefined,
  current: BroilerFeedOwner | null | undefined,
): boolean {
  return !!draftOwner && canWriteBroilerFeed(current) && current.key === draftOwner.key;
}

/**
 * A COMPLETE lbs entry: trimmed, non-empty, finite and >= 0, as Number() reads
 * it. Everything else — blank, '-', '1e', '.', '12abc', negative, Infinity —
 * is incomplete and returns null; it is never coerced to 0. A typed 0 is 0.
 * Order and count quantities are plain lbs; no price-decimal rule applies.
 */
export function parseFeedLbs(raw: string | null | undefined): number | null {
  const text = String(raw ?? '').trim();
  if (text === '') return null;
  const n = Number(text);
  if (!Number.isFinite(n) || n < 0) return null;
  return n === 0 ? 0 : n;
}

export type BroilerOrderDraft = Readonly<{owner: BroilerFeedOwner | null; ym: string; raw: string}>;
export type BroilerOrderPayload = Readonly<{ym: string; orderForMonth: number}>;

/**
 * One revision of one feed type's physical count. `typed` is false until the
 * operator has typed in the lbs field this mount; `date` is the day the
 * revision was typed; `type` is the feed type the revision belongs to, frozen
 * with the intent so a later type switch cannot re-date or repaint it.
 */
export type BroilerCountDraft = Readonly<{
  owner: BroilerFeedOwner | null;
  type: string;
  raw: string;
  typed: boolean;
  includes: boolean;
  date: string;
}>;
export type BroilerCountPayload = Readonly<{
  type: string;
  count: number;
  date: string;
  includesCurrentMonthDelivery: boolean;
}>;

export const BROILER_FEED_NO_WRITE_ACCESS = "This account can't save feed changes. Your entry is kept.";
export const BROILER_ORDER_INCOMPLETE = 'Enter an order of 0 lbs or more.';
export const BROILER_COUNT_BLANK = 'Enter the lbs on hand.';
export const BROILER_COUNT_NEGATIVE = "Lbs on hand can't be negative.";
export const BROILER_COUNT_UNTOUCHED = "Enter today's lbs on hand to save this.";

/** Why this order revision may not be written, or null when it may. A blank
 * with nothing persisted or acked for that month is "nothing to save", not
 * an error: a field typed and then cleared settles without a write and never
 * blocks Done or navigation. A blank over an existing value is an attempt to
 * clear it — never automatic, so it stays incomplete. */
export function broilerOrderDraftProblem(
  draft: BroilerOrderDraft,
  current: BroilerFeedOwner | null,
  existing: unknown = undefined,
): string | null {
  if (!isCurrentBroilerFeedWriter(draft.owner, current)) return BROILER_FEED_NO_WRITE_ACCESS;
  if (draft.raw.trim() === '') return existing == null ? null : BROILER_ORDER_INCOMPLETE;
  return parseFeedLbs(draft.raw) === null ? BROILER_ORDER_INCOMPLETE : null;
}

/**
 * Why this count revision may not be written, or null when it may. An
 * untouched count with only the delivery toggle changed is incomplete: writing
 * it would re-date an older count to today. Toggled back to the confirmed
 * value, it has nothing to save and settles without a request.
 */
export function broilerCountDraftProblem(
  draft: BroilerCountDraft,
  current: BroilerFeedOwner | null,
  confirmedIncludes: boolean,
): string | null {
  if (!isCurrentBroilerFeedWriter(draft.owner, current)) return BROILER_FEED_NO_WRITE_ACCESS;
  if (!draft.typed) return draft.includes === confirmedIncludes ? null : BROILER_COUNT_UNTOUCHED;
  const text = draft.raw.trim();
  if (parseFeedLbs(text) !== null) return null;
  return text !== '' && Number(text) < 0 ? BROILER_COUNT_NEGATIVE : BROILER_COUNT_BLANK;
}

// ── Confirmed-revision acks ─────────────────────────────────────────────────
// An ack is the server's confirmed value for one owner's revision. Overlays
// apply only when the ack's owner is still the rendering owner, so a stale
// account's confirmation can never repaint a newer session.

export type BroilerOrderAcks = Readonly<{ownerKey: string; months: Readonly<Record<string, number>>}>;
export type BroilerCountAck = Readonly<{ownerKey: string; entry: BroilerCountPayload}>;

/** One poultry map with the CURRENT owner's confirmed months laid over it. */
export function ackedBroilerOrderMap<MapT extends Record<string, unknown>>(
  base: MapT,
  acks: BroilerOrderAcks | null,
  ownerKey: string,
): MapT {
  if (!acks || acks.ownerKey !== ownerKey) return base;
  return {...base, ...acks.months};
}

/** The route's per-type counts with the CURRENT owner's confirmed entries
 * laid over it. Every other type's entry is the route's. */
export function ackedBroilerFeedInventory<InventoryT extends Record<string, unknown>>(
  base: InventoryT,
  ack: BroilerCountAck | null,
  ownerKey: string,
): InventoryT {
  if (!ack || ack.ownerKey !== ownerKey) return base;
  return {
    ...base,
    [ack.entry.type]: {
      count: ack.entry.count,
      date: ack.entry.date,
      includesCurrentMonthDelivery: ack.entry.includesCurrentMonthDelivery,
    },
  };
}

// These writes report to the keyed saveStatus store through the controller
// binding below. The legacy AuthContext sink is one unscoped value, so an old
// account's completion could paint the new account's header.
export const NO_LEGACY_SAVE_STATUS: SaveStatusSink = () => {};

export type BroilerFeedSendOutcome =
  | Readonly<{kind: 'confirmed'}>
  | Readonly<{kind: 'rejected'; code: string | null; message: string}>
  | Readonly<{kind: 'unconfirmed'; message: string}>
  | Readonly<{kind: 'not-sent'; message: string}>;

export const BROILER_COUNT_UNCONFIRMED = "The count wasn't confirmed as saved. Your entry is kept; retry.";
export const BROILER_ORDER_UNCONFIRMED = "The order wasn't confirmed as saved. Your entry is kept; retry.";
export const BROILER_ORDER_NOT_SENT = "Couldn't read the current feed orders, so nothing was saved. Retry.";

/** The operation's typed outcome, as the replay rules need it. A `refused`
 * reply is the server's definite "not written" and settles; only a write no
 * reply confirmed is held for same-id replay before anything newer goes out.
 * `subject` only picks the truthful wording. */
export function broilerFeedSendOutcome(
  subject: 'order' | 'count',
  outcome: AppStoreConfigOpOutcome,
): BroilerFeedSendOutcome {
  if (outcome.status === 'saved' || outcome.status === 'no_change') {
    return Object.freeze({kind: 'confirmed'}) as BroilerFeedSendOutcome;
  }
  if (outcome.status === 'not_sent') {
    return FENCE_BLOCKED;
  }
  if (outcome.status === 'rejected') {
    return Object.freeze({
      kind: 'rejected',
      code: outcome.code,
      message: appStoreConfigRejectionMessage(outcome),
    });
  }
  if (outcome.status === 'refused') {
    const message =
      subject === 'count'
        ? 'The server refused this count. Your entry is kept.'
        : 'The server refused this order. Your entry is kept.';
    return Object.freeze({kind: 'rejected', code: 'refused', message});
  }
  return Object.freeze({
    kind: 'unconfirmed',
    message: outcome.message || (subject === 'count' ? BROILER_COUNT_UNCONFIRMED : BROILER_ORDER_UNCONFIRMED),
  });
}

/** The REAL order write, as the autosave's send: a fail-closed read of
 * ppp-feed-orders-v1 taken at save time, then `feed_orders.patch` with the
 * revision's own request id. The patch names ONLY `orderKey` — the pig map
 * and the other poultry maps are carried by the server-side locked merge,
 * never sent from this mount's snapshot. An unavailable read sends nothing
 * (`not-sent`): a refusal is recoverable, a half-blob is not. */
export function makeBroilerOrderSender(sb: unknown, orderKey: BroilerFeedOrderKey, fence?: BroilerSendFence) {
  return async function sendBroilerOrderPatch(
    payload: BroilerOrderPayload,
    requestId: string,
  ): Promise<BroilerFeedSendOutcome> {
    if (fence && !(await fence())) return FENCE_BLOCKED;
    const patch = {[orderKey]: {[payload.ym]: payload.orderForMonth}};
    const plan = await planFeedOrdersSave(
      () => readFreshFeedOrders(sb as Parameters<typeof readFreshFeedOrders>[0]),
      patch,
    );
    if (plan.kind !== 'save') return Object.freeze({kind: 'not-sent', message: BROILER_ORDER_NOT_SENT});
    // The fresh read is an await: re-check the live writer between it and the
    // RPC, or a switched account's session could carry this write.
    if (fence && !(await fence())) return FENCE_BLOCKED;
    const outcome = await patchFeedOrdersOnServer(
      sb as Parameters<typeof patchFeedOrdersOnServer>[0],
      patch,
      NO_LEGACY_SAVE_STATUS,
      requestId,
      fence
        ? {
            ...(Object.hasOwn(fence, 'expectedActor') ? {expectedActor: fence.expectedActor} : {}),
            beforeAttempt: fence,
          }
        : undefined,
    );
    return broilerFeedSendOutcome('order', outcome);
  };
}

/** The REAL count write: one `poultry_feed_inventory.save` operation built
 * from the imported key constant, carrying the revision's typed date and
 * delivery flag, answered by its typed outcome. */
export function makeBroilerCountSender(sb: unknown, fence?: BroilerSendFence) {
  return async function sendBroilerCount(
    payload: BroilerCountPayload,
    requestId: string,
  ): Promise<BroilerFeedSendOutcome> {
    if (fence && !(await fence())) return FENCE_BLOCKED;
    const outcome = await runAppStoreConfigOperationWithStatus(
      sb as Parameters<typeof runAppStoreConfigOperationWithStatus>[0],
      {
        requestId,
        operation: 'poultry_feed_inventory.save',
        target: {key: POULTRY_FEED_INVENTORY_APP_STORE_KEY},
        expected: {},
        change: {
          type: payload.type,
          entry: {
            count: payload.count,
            date: payload.date,
            includesCurrentMonthDelivery: payload.includesCurrentMonthDelivery,
          },
        },
      },
      NO_LEGACY_SAVE_STATUS,
      undefined,
      fence
        ? {
            ...(Object.hasOwn(fence, 'expectedActor') ? {expectedActor: fence.expectedActor} : {}),
            beforeAttempt: fence,
          }
        : undefined,
    );
    return broilerFeedSendOutcome('count', outcome);
  };
}

// Under canonical migration 260 (feedReceiptOperationApi), ONLY a bound
// receipt settles a request: `confirmed` (`applied`/`no_change`) or `rejected`
// with `code === 'refused'` (from a bound receipt with `outcome: 'refused'`).
// Raw raised SQLSTATEs (42501, 22023, WCF01, ...) never settle: the held
// request stays held and prevents N+1 from overtaking it.
export const SETTLING_REJECTIONS_ORDER: ReadonlySet<string> = new Set(['refused']);
export const SETTLING_REJECTIONS_COUNT: ReadonlySet<string> = new Set(['refused']);

function settlesWith(settling: ReadonlySet<string>, outcome: BroilerFeedSendOutcome) {
  return (
    outcome.kind === 'confirmed' || (outcome.kind === 'rejected' && outcome.code !== null && settling.has(outcome.code))
  );
}

/** The live-writer fence checked immediately before EVERY RPC. It is BOUND to
 * one controller's own draft owner (captured at controller creation) and
 * compares that binding against the FULL live identity — subject, effective
 * role, preview and session epoch, with the SDK session as the subject source.
 * A retired controller can never borrow a new account's authority. */
export type BroilerSendFence = (() => Promise<boolean> | boolean) & {
  expectedActor?: string | null;
};

export type BroilerHeldDrainOutcome<Payload> =
  | Readonly<{kind: 'confirmed'; payload?: Payload}>
  | Exclude<BroilerFeedSendOutcome, {kind: 'confirmed'}>;

/** The FULL live writer identity the SDK session proves, as the mount reads
 * it after every await. */
export type LiveIdentity = Readonly<{
  subjectId: string;
  effectiveRole: string;
  previewRole: string | null;
  sessionEpoch: number;
}>;

const FENCE_BLOCKED = Object.freeze({
  kind: 'not-sent',
  message: 'The signed-in account changed before sending. Your entry is kept.',
} as const);

/**
 * Serial sends for ONE key (the controller already runs one at a time) that
 * never let a newer intent overtake an unsettled older one. An outcome that is
 * neither confirmed nor a settling rejection leaves its request HELD; the next
 * send replays the held request first, with its own id and payload, and goes
 * on only once that has settled. `not-sent` (the fresh read failed, the fence
 * refused, nothing reached the server) changes nothing. The fence runs before
 * the held replay AND before the new revision: an account switch during the
 * replay's await stops the chain before the next request goes out under
 * another account's session.
 */
export function createHeldReplaySender<Payload>(
  send: (payload: Payload, requestId: string) => Promise<BroilerFeedSendOutcome>,
  settles: (outcome: BroilerFeedSendOutcome) => boolean = (outcome) => settlesWith(SETTLING_REJECTIONS_ORDER, outcome),
  fence?: BroilerSendFence,
) {
  let held: {requestId: string; payload: Payload} | null = null;
  return {
    async send(
      payload: Payload,
      requestId: string,
      onHeldConfirmed?: (heldPayload: Payload) => void,
    ): Promise<BroilerFeedSendOutcome> {
      if (held && held.requestId !== requestId) {
        if (fence && !(await fence())) return FENCE_BLOCKED;
        const target = held;
        const prior = await send(target.payload, target.requestId);
        if (!settles(prior)) return prior;
        held = null;
        if (prior.kind === 'confirmed') {
          if (!fence || (await fence())) {
            onHeldConfirmed?.(target.payload);
          }
        }
      }
      if (fence && !(await fence())) return FENCE_BLOCKED;
      const outcome = await send(payload, requestId);
      if (outcome.kind === 'not-sent') return outcome;
      held = settles(outcome) ? null : {requestId, payload};
      return outcome;
    },
    heldRequestId: () => (held ? held.requestId : null),
    /** Settle the held intent NOW (same id, same payload), fenced like any
     * send. A blank "nothing to save" must drain an unknown N first — it may
     * still land, and the UI must not claim nothing-to-save over it. */
    async drainHeld(): Promise<BroilerHeldDrainOutcome<Payload>> {
      if (!held) return Object.freeze({kind: 'confirmed'});
      if (fence && !(await fence())) return FENCE_BLOCKED;
      const target = held;
      const outcome = await send(target.payload, target.requestId);
      if (outcome.kind !== 'not-sent' && settles(outcome)) held = null;
      if (outcome.kind === 'confirmed') {
        return Object.freeze({kind: 'confirmed', payload: target.payload});
      }
      return outcome;
    },
  };
}
export type HeldReplaySender<Payload> = ReturnType<typeof createHeldReplaySender<Payload>>;

/**
 * The controller's states on the keyed saveStatus store. Pending and saving
 * are unconfirmed work ("Saving…"); invalid and failed keep the draft and are
 * not saved ("Not Saved — Retry"); idle is "Saved" only when the server
 * confirmed a write since the last report. A settle with nothing written (a
 * toggle put back) releases the key instead, and so does teardown — only
 * while this binding's attempt is still the newest on the key.
 */
export function createBroilerFeedStatusBinding(key: string, retry: () => void) {
  let attempt: number | null = null;
  let wrote = false;
  function release() {
    if (attempt !== null && isLatestSave(key, attempt)) forgetSave(key);
    attempt = null;
    wrote = false;
  }
  return {
    markWrote() {
      wrote = true;
    },
    onState(state: DraftSaveState) {
      if (state === 'pending' || state === 'saving') {
        attempt = reportSaving(key);
      } else if (state === 'idle') {
        if (attempt === null) return;
        if (!wrote) return release();
        reportSaved(key, attempt);
        attempt = null;
        wrote = false;
      } else {
        if (attempt === null) attempt = reportSaving(key);
        reportSaveFailed(key, retry, attempt);
      }
    },
    dispose: release,
  };
}

export type BroilerFeedPersistIO<Payload> = Readonly<{
  sender: HeldReplaySender<Payload>;
  /** One id per revision: minted on its first send, reused by every retry. */
  requestIdFor: (draft: object) => string;
  markWrote: () => void;
}>;

// ── The persist steps the view binds in, verbatim ───────────────────────────
// These factories ARE the view's persist closures — the custody rules (judge
// the owner before sending and again after the reply, keep the exact payload
// and id for a held replay, ack only the confirmed revision of the CURRENT
// owner) live here, not in a page-local copy, so tests exercise the same
// closures the operator's keystrokes drive.

export type BroilerOrderPersistContext = Readonly<{
  /** The CURRENT owner at call time, or null while auth is unresolved. */
  getCurrentOwner: () => BroilerFeedOwner | null;
  /** False once this mount has gone away: a late reply then publishes nothing. */
  isAlive: () => boolean;
  /** Why the current revision is not being written (owner-gated upstream). */
  onFailure: (ownerKey: string, message: string) => void;
  onClearFailure: () => void;
  /** The server-confirmed revision, laid over the CURRENT owner's view only. */
  onAck: (ownerKey: string, ym: string, value: number) => void;
}>;

/** One feed key's order persist: one revision of one month, written to the
 * month the draft was typed for. The number sent is the number then shown; an
 * incomplete entry never gets this far (validate refuses it) and none is ever
 * coerced to 0 or sent as a clear. */
export function makeBroilerOrderPersist(orderKey: BroilerFeedOrderKey, ctx: BroilerOrderPersistContext) {
  return async function persistBroilerOrder(
    draft: BroilerOrderDraft,
    io: BroilerFeedPersistIO<BroilerOrderPayload>,
  ): Promise<boolean> {
    // Validate only schedules a blank draft when nothing is persisted or
    // acked for that month: clearing before the first save settles without a
    // write and unblocks Done (the count's toggle-back rule, for orders).
    // An UNKNOWN held intent for this key is the exception: it may still
    // land, so it is drained (same id, same payload) before anything claims
    // "nothing to save" — and only a definite answer settles the blank.
    if (draft.raw.trim() === '') {
      if (!io.sender.heldRequestId()) return true;
      const drained = await io.sender.drainHeld();
      if (!ctx.isAlive() || !draft.owner || !isCurrentBroilerFeedWriter(draft.owner, ctx.getCurrentOwner())) {
        return false;
      }
      if (drained.kind === 'confirmed') {
        if (drained.payload) {
          ctx.onAck(draft.owner.key, drained.payload.ym, drained.payload.orderForMonth);
        }
        io.markWrote();
        ctx.onClearFailure();
        return true;
      }
      if (drained.kind === 'not-sent') return false;
      ctx.onFailure(draft.owner.key, drained.message);
      return false;
    }
    const orderForMonth = parseFeedLbs(draft.raw);
    if (orderForMonth === null || !draft.owner || !isCurrentBroilerFeedWriter(draft.owner, ctx.getCurrentOwner())) {
      return false;
    }
    const draftOwnerKey = draft.owner.key;
    const outcome = await io.sender.send({ym: draft.ym, orderForMonth}, io.requestIdFor(draft), (heldPayload) => {
      if (ctx.isAlive() && isCurrentBroilerFeedWriter(draft.owner, ctx.getCurrentOwner())) {
        io.markWrote();
        ctx.onAck(draftOwnerKey, heldPayload.ym, heldPayload.orderForMonth);
      }
    });
    if (!ctx.isAlive() || !isCurrentBroilerFeedWriter(draft.owner, ctx.getCurrentOwner())) return false;
    if (outcome.kind !== 'confirmed') {
      ctx.onFailure(draftOwnerKey, outcome.message);
      return false;
    }
    ctx.onClearFailure();
    io.markWrote();
    ctx.onAck(draftOwnerKey, draft.ym, orderForMonth);
    return true;
  };
}

export type BroilerCountPersistContext = Readonly<{
  getCurrentOwner: () => BroilerFeedOwner | null;
  isAlive: () => boolean;
  onFailure: (ownerKey: string, message: string) => void;
  onClearFailure: () => void;
  onAck: (ownerKey: string, entry: BroilerCountPayload) => void;
}>;

/** The physical count persist: what is on site, dated the day the revision
 * was typed, under the feed type the revision was typed for. A toggle moved
 * with an untouched count settles without a write — writing it would re-date
 * an older count to today. */
export function makeBroilerCountPersist(ctx: BroilerCountPersistContext) {
  return async function persistBroilerCount(
    draft: BroilerCountDraft,
    io: BroilerFeedPersistIO<BroilerCountPayload>,
  ): Promise<boolean> {
    if (!draft.owner || !isCurrentBroilerFeedWriter(draft.owner, ctx.getCurrentOwner())) return false;
    if (!draft.typed) return true;
    const count = parseFeedLbs(draft.raw);
    if (count === null) return false;
    const draftOwnerKey = draft.owner.key;
    const entry: BroilerCountPayload = {
      type: draft.type,
      count,
      date: draft.date,
      includesCurrentMonthDelivery: !!draft.includes,
    };
    const outcome = await io.sender.send(entry, io.requestIdFor(draft), (heldEntry) => {
      if (ctx.isAlive() && isCurrentBroilerFeedWriter(draft.owner, ctx.getCurrentOwner())) {
        io.markWrote();
        ctx.onAck(draftOwnerKey, heldEntry);
      }
    });
    if (!ctx.isAlive() || !isCurrentBroilerFeedWriter(draft.owner, ctx.getCurrentOwner())) return false;
    if (outcome.kind !== 'confirmed') {
      ctx.onFailure(draftOwnerKey, outcome.message);
      return false;
    }
    ctx.onClearFailure();
    io.markWrote();
    ctx.onAck(draftOwnerKey, entry);
    return true;
  };
}

export type BroilerFeedAutosaveOptions<Draft, Payload> = {
  statusKey: string;
  requestPrefix: string;
  validate: (draft: Draft) => boolean;
  persist: (draft: Draft, io: BroilerFeedPersistIO<Payload>) => Promise<boolean>;
  send: (payload: Payload, requestId: string) => Promise<BroilerFeedSendOutcome>;
  /** Checked immediately before every RPC: current owner AND SDK subject. */
  authorize?: BroilerSendFence;
  /** Which rejections settle this writer (order vs count 22023 semantics). */
  settlingRejections?: ReadonlySet<string>;
  onState?: (state: DraftSaveState) => void;
  debounceMs?: number;
};

/** One owner's autosave: its own controller, held-replay sender and status
 * binding. Inert until its first update. */
export function createBroilerFeedAutosave<Draft, Payload>(options: BroilerFeedAutosaveOptions<Draft, Payload>) {
  const binding = createBroilerFeedStatusBinding(options.statusKey, () => void controller.flush());
  const ids = new WeakMap<object, string>();
  const io: BroilerFeedPersistIO<Payload> = {
    sender: createHeldReplaySender(
      options.send,
      options.settlingRejections
        ? (outcome) => settlesWith(options.settlingRejections as ReadonlySet<string>, outcome)
        : undefined,
      options.authorize,
    ),
    requestIdFor(draft) {
      let id = ids.get(draft);
      if (!id) {
        id = newAppStoreConfigRequestId(options.requestPrefix);
        ids.set(draft, id);
      }
      return id;
    },
    markWrote: () => binding.markWrote(),
  };
  const controller = createConfirmedDraftAutosave<Draft>({
    validate: options.validate,
    persist: (draft) => options.persist(draft, io),
    onState: (state) => {
      binding.onState(state);
      options.onState?.(state);
    },
    debounceMs: options.debounceMs,
  });
  return {controller, io, binding};
}

/**
 * useConfirmedDraftAutosave's shape (same controller, same draft guard), with
 * the controller keyed by owner: a new `ownerKey` suspends and disposes the
 * old one and starts clean, so nothing typed under one owner survives into
 * the next. `state` is only ever the current owner's.
 *
 * THE FENCE IS BOUND AT CREATION. Each controller captures the FULL owner it
 * was created for (subject, effective role, preview, session epoch); its
 * authorize compares that binding against the live identity read through
 * `readLiveIdentity` — SDK subject included, re-checked in full AFTER the
 * SDK await. `latest` forwards only the live READER, never a new owner's
 * credentials: a retired controller can never borrow the new account's
 * authority. The send factories receive the same bound fence, so the
 * fresh-read→RPC gap inside them is fenced with the same binding.
 */
export function useBroilerFeedAutosave<Draft, Payload>(
  options: Omit<BroilerFeedAutosaveOptions<Draft, Payload>, 'statusKey' | 'onState' | 'authorize' | 'send'> & {
    guardKey: string;
    ownerKey: string;
    /** The FULL current owner; a new identity swaps the controller. */
    owner: BroilerFeedOwner | null;
    /** Build this writer's send chain around the controller's OWN bound
     * fence (called once per owner at controller creation). */
    makeSend: (fence: BroilerSendFence) => (payload: Payload, requestId: string) => Promise<BroilerFeedSendOutcome>;
    /** The FULL live identity — SDK session subject plus the mount's current
     * owner fields — or null when unreadable/changed. */
    readLiveIdentity: () => Promise<LiveIdentity | null>;
    isBusy?: () => boolean;
  },
) {
  const latest = useRef(options);
  latest.current = options;
  const {guardKey, ownerKey, requestPrefix} = options;
  const [snapshot, setSnapshot] = useState<{ownerKey: string; state: DraftSaveState}>({ownerKey, state: 'idle'});
  // Built per OWNER IDENTITY KEY — never per render. The view re-creates the
  // owner object and the makeSend closure every render; keying the memo on
  // those identities would retire the controller (its debounce, held replay
  // and dirty custody) on every keystroke. The key IS the full writer
  // identity (subject|role|preview|epoch), so the binding captured here from
  // `latest` at creation is exactly the writer this controller serves, and a
  // real identity change produces a new key and a new controller.
  const autosave = useMemo(() => {
    const boundOwner = latest.current.owner;
    const boundAuthorize: BroilerSendFence = Object.assign(
      async () => {
        if (!boundOwner) return false;
        const identity = await latest.current.readLiveIdentity();
        if (!identity) return false;
        return (
          identity.subjectId === boundOwner.subjectId &&
          identity.effectiveRole === boundOwner.effectiveRole &&
          identity.previewRole === boundOwner.previewRole &&
          identity.sessionEpoch === boundOwner.sessionEpoch
        );
      },
      {expectedActor: boundOwner?.subjectId ?? null},
    );
    return createBroilerFeedAutosave<Draft, Payload>({
      statusKey: guardKey + '|' + ownerKey,
      requestPrefix,
      validate: (draft) => latest.current.validate(draft),
      persist: (draft, io) => latest.current.persist(draft, io),
      send: latest.current.makeSend(boundAuthorize),
      authorize: boundAuthorize,
      settlingRejections: latest.current.settlingRejections,
      onState: (state) => setSnapshot({ownerKey, state}),
      debounceMs: options.debounceMs,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- one controller per identity key; live values are read through `latest`.
  }, [guardKey, ownerKey, requestPrefix]);
  useAppDraftGuard({
    ownerKey: guardKey,
    isUnsafeToLeave: () => autosave.controller.isDirty() || Boolean(latest.current.isBusy?.()),
    settleBeforeLeave: () => (latest.current.isBusy?.() ? Promise.resolve(false) : autosave.controller.flush()),
  });
  useLayoutEffect(() => {
    autosave.controller.resume();
    return () => {
      autosave.controller.suspend();
      autosave.binding.dispose();
    };
  }, [autosave]);
  return {controller: autosave.controller, state: snapshot.ownerKey === ownerKey ? snapshot.state : 'idle'};
}
